import React, { useEffect, useState } from 'react'
import apiClient from '../../apis/api'
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Pagination as MuiPagination,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'

const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleString();
}

function AskParisConversations() {
  const [conversations, setConversations] = useState([])
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [total, setTotal] = useState(0)
  const [selectedSession, setSelectedSession] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)

  const totalPages = Math.max(1, Math.ceil(total / limit))
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const fetchConversations = async (p = 1) => {
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams()
      if (startDate) qs.set('startDate', startDate)
      if (endDate) qs.set('endDate', endDate)
      qs.set('page', p)
      qs.set('limit', limit)

      const resp = await apiClient.get(`/openAi/paris/conversations?${qs.toString()}`)
      setConversations(resp.data || resp || [])
      setTotal(resp.total || 0)
      setPage(resp.page || p)
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchConversations(1) /* eslint-disable-line */ }, [limit])

  const openSession = async (sessionId) => {
    setLoading(true)
    try {
      const resp = await apiClient.get(`/openAi/paris/conversations/${encodeURIComponent(sessionId)}`)
      if (resp && resp.data) {
        setSelectedSession(resp.data)
        setModalOpen(true)
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load session')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => { e.preventDefault(); fetchConversations(1) }

  const handlePageChange = (_, value) => { setPage(value); fetchConversations(value) }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h3" fontWeight={'bold'}>Paris Conversations</Typography>
          <Typography variant="body2" color="text.secondary">{total} results</Typography>
        </Stack>

        <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <TextField label="Start date" type="date" size="small" value={startDate} onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="End date" type="date" size="small" value={endDate} onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField select size="small" label="Page size" value={limit} onChange={e => setLimit(Number(e.target.value))} sx={{ width: 120 }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </TextField>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button type="submit" variant="contained" color="error" size="small">{loading ? <CircularProgress size={18} color="inherit" /> : 'Filter'}</Button>
            <Button size="small" onClick={() => { setStartDate(''); setEndDate(''); setPage(1); fetchConversations(1); }}>Clear</Button>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {loading ? (
          <Grid item xs={12}><Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box></Grid>
        ) : conversations && conversations.length ? (
          conversations.map(conv => (
            <Grid item xs={12} md={6} key={conv._id}>
              <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} elevation={1}>
                <Box sx={{ pr: 2, flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{conv.sessionId}</Typography>
                  <Typography variant="body2" color="text.secondary">{conv.email || '—'}</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      whiteSpace: 'normal',
                    }}
                  >
                    {conv.lastMessage || '—'}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', width: 190 }}>
                  <Typography variant="caption" color="text.secondary">{formatDate(conv.createdAt)}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => openSession(conv.sessionId)}>View</Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}><Paper sx={{ p: 4, textAlign: 'center' }}>No conversations found</Paper></Grid>
        )}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <MuiPagination count={totalPages} page={page} onChange={handlePageChange} color="standard" />
      </Box>

      {/** responsive fullScreen on small devices, with small side margins on larger screens */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullWidth
        maxWidth="xl"
        fullScreen={isMobile}
        PaperProps={{ sx: isMobile ? { mx: 1, width: 'calc(100% - 16px)', maxWidth: 'calc(100vw - 16px)' } : { mx: 6, width: 'calc(100% - 120px)', maxWidth: 'calc(100vw - 120px)' } }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          {selectedSession?.sessionId || selectedSession?._id}
          <IconButton aria-label="close" onClick={() => setModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">User: {selectedSession?.userId || selectedSession?.email || '—'} • Created: {formatDate(selectedSession?.createdAt)}</Typography>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {selectedSession?.messages && selectedSession.messages.length ? (
              selectedSession.messages.map((m, i) => {
                const isUser = m.role === 'user';
                const author = isUser ? ('User' || selectedSession?.userId || selectedSession?.email) : 'PARIS';
                return (
                  <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{author}</Typography>
                    <Paper sx={{ p: 2, background: isUser ? '#f5faff' : '#fff6e6', whiteSpace: 'pre-wrap', borderRadius: 2 }} elevation={0}>
                      <Typography variant="body2">{m.content}</Typography>
                    </Paper>
                    <Typography variant="caption" color="text.secondary">{formatDate(m.timestamp || m.createdAt)}</Typography>
                  </Box>
                )
              })
            ) : (
              <Typography color="text.secondary">No messages for this session.</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AskParisConversations